import React, { useContext, useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Stack,
} from '@mui/material';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import { toast } from 'react-toastify';
import useInternalBooking from '../../../../hooks/useInternalBooking';
import SocketIOContext from '../../../../context/socketIOContext';

// How long we wait for the counter agent's result before giving up. The
// locationserver itself times out after 90s, so this must be longer.
const RESULT_TIMEOUT_MS = 120000;

const UNMAPPED_REASON_LABELS = {
  NO_COPAGO_ARTICLE_MAPPED: 'Kein Copago-Artikel zugeordnet',
  UNSUPPORTED_LINE_ITEM_TYPE: 'Positionstyp nicht übertragbar',
  COPAGO_ARTICLE_NOT_AVAILABLE: 'Copago-Artikel inaktiv oder gelöscht',
};

function PosTransferDialog({ open, onClose, booking }) {
  const { getPosTransferPreview, sendPosTransfer } = useInternalBooking();
  const socketCtx = useContext(SocketIOContext);

  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedCounter, setSelectedCounter] = useState('');
  const [sending, setSending] = useState(false);
  const [waitingForResult, setWaitingForResult] = useState(false);
  const correlationIdRef = useRef(null);
  const timeoutRef = useRef(null);

  useEffect(() => {
    if (!open || !booking?._id) return;
    let cancelled = false;
    setPreview(null);
    setLoading(true);
    (async () => {
      try {
        const response = await getPosTransferPreview({ bookingId: booking._id });
        if (cancelled || !response) return;
        setPreview(response);
        setSelectedCounter(response.counters?.[0]?.name || '');
      } catch (err) {
        if (!cancelled) {
          toast.error('Fehler beim Laden der Kassenübertragung-Vorschau');
          onClose?.();
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, booking?._id]);

  // Listen for the asynchronous transfer result pushed via Socket.io
  useEffect(() => {
    if (!waitingForResult || !socketCtx || typeof socketCtx.on !== 'function') return undefined;
    const off = socketCtx.on('posTransferResult', payload => {
      if (!payload || payload.correlationId !== correlationIdRef.current) return;
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      setWaitingForResult(false);
      if (payload.success) {
        toast.success(`Buchung erfolgreich an Kasse "${payload.counter}" übertragen`);
        onClose?.();
      } else {
        toast.error(
          `Kassenübertragung fehlgeschlagen${payload.message ? `: ${payload.message}` : ''}`
        );
      }
    });
    return () => {
      if (typeof off === 'function') off();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [waitingForResult, socketCtx]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const handleSend = async () => {
    if (!selectedCounter) return;
    setSending(true);
    try {
      const response = await sendPosTransfer(
        { bookingId: booking._id, counter: selectedCounter },
        'Fehler beim Senden der Kassenübertragung'
      );
      if (response?.correlationId) {
        correlationIdRef.current = response.correlationId;
        setWaitingForResult(true);
        timeoutRef.current = setTimeout(() => {
          setWaitingForResult(false);
          toast.warning(
            'Keine Antwort von der Kasse erhalten. Bitte prüfen, ob die Positionen angekommen sind.'
          );
        }, RESULT_TIMEOUT_MS);
      }
    } catch (err) {
      const code = err?.code || err?.data?.code;
      if (code === 'NO_TRANSFERABLE_LINE_ITEMS') {
        toast.error('Keine übertragbaren unbezahlten Positionen vorhanden');
      } else if (code === 'POS_TRANSFER_NOT_AVAILABLE') {
        toast.error('Kassenübertragung derzeit nicht verfügbar (keine Verbindung)');
      } else {
        toast.error('Fehler beim Senden der Kassenübertragung');
      }
    } finally {
      setSending(false);
    }
  };

  const items = preview?.items || [];
  const unmapped = preview?.unmapped || [];
  const counters = preview?.counters || [];

  return (
    <Dialog open={open} onClose={waitingForResult ? undefined : onClose} maxWidth='sm' fullWidth>
      <DialogTitle>An Kasse übertragen</DialogTitle>
      <DialogContent>
        {loading && (
          <Stack alignItems='center' sx={{ py: 3 }}>
            <CircularProgress size={28} />
          </Stack>
        )}
        {!loading && preview && (
          <Stack spacing={2} sx={{ mt: 1 }}>
            {counters.length === 0 && (
              <Alert severity='warning'>
                Für dieses Entertainment sind keine Kassen hinterlegt. Kassen können in der
                Entertainment-Verwaltung gepflegt werden.
              </Alert>
            )}
            {counters.length > 0 && (
              <FormControl fullWidth size='small'>
                <InputLabel id='pos-transfer-counter-label'>Kasse</InputLabel>
                <Select
                  labelId='pos-transfer-counter-label'
                  label='Kasse'
                  value={selectedCounter}
                  onChange={event => setSelectedCounter(event.target.value)}
                  disabled={sending || waitingForResult}
                >
                  {counters.map(counter => (
                    <MenuItem key={counter._id || counter.name} value={counter.name}>
                      {counter.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            {items.length > 0 ? (
              <Table size='small'>
                <TableHead>
                  <TableRow>
                    <TableCell>Position</TableCell>
                    <TableCell align='right'>Anzahl</TableCell>
                    <TableCell align='right'>Artikel-Nr.</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map(item => (
                    <TableRow key={item.lineItemId}>
                      <TableCell>{item.description}</TableCell>
                      <TableCell align='right'>{item.count}</TableCell>
                      <TableCell align='right'>{item.copagoSKU}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <Alert severity='info'>Keine übertragbaren unbezahlten Positionen vorhanden.</Alert>
            )}
            {unmapped.length > 0 && (
              <Alert severity='warning'>
                <Typography variant='body2' sx={{ fontWeight: 600, mb: 0.5 }}>
                  Nicht übertragbare Positionen:
                </Typography>
                {unmapped.map(item => (
                  <Typography key={item.lineItemId} variant='body2'>
                    {item.count}× {item.description} —{' '}
                    {UNMAPPED_REASON_LABELS[item.reason] || item.reason}
                  </Typography>
                ))}
              </Alert>
            )}
            {waitingForResult && (
              <Alert severity='info' icon={<CircularProgress size={18} />}>
                Übertragung läuft — warte auf Bestätigung der Kasse…
              </Alert>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={waitingForResult}>
          Schließen
        </Button>
        <Button
          variant='contained'
          startIcon={<PointOfSaleIcon />}
          onClick={handleSend}
          disabled={
            loading ||
            sending ||
            waitingForResult ||
            items.length === 0 ||
            !selectedCounter
          }
        >
          Übertragen
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default PosTransferDialog;
